import { useEffect, useState } from "react";
import { Button, Checkbox, Divider, Empty, Input, Space, Switch, Tag, Tooltip, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import ApiService from "../../core/service/api-service";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";

/**
 * Çekimi listeye ekleme ekranı — grid'de çekim id'sine tıklanınca açılan menüden gelir.
 *
 * Kutucuk işaretlemek ANINDA ekler/çıkarır (Gmail etiketleri gibi); açıklama alanı odaktan
 * çıkınca kaydedilir. Böylece "Kaydet" beklemeden geri bildirim alınır.
 *
 * Yetki: hangi listeye eklenebileceği ve hangi üyeliğin kaldırılabileceği SUNUCUDAN gelen
 * bayraklarla belirlenir (rights.canAddItem / membership.canRemove) — burada izin mantığı
 * yeniden hesaplanmaz.
 */
export default function AddToListModal({ studyId, onChanged }) {
    const { t } = useTranslation();
    const { hasPermission } = useUser();

    const [lists, setLists] = useState([])
    const [memberships, setMemberships] = useState([])
    const [descriptions, setDescriptions] = useState({})
    const [busyListId, setBusyListId] = useState(null)
    const [newName, setNewName] = useState('')
    const [newPublic, setNewPublic] = useState(false)
    const [creating, setCreating] = useState(false)

    const canCreate = hasPermission(PERMISSIONS.LIST_CREATE);
    const canAdd = hasPermission(PERMISSIONS.LIST_ITEM_ADD);

    const load = async () => {
        const [listRes, memberRes] = await Promise.all([
            ApiService.StudyList.GetList(),
            ApiService.StudyList.GetStudyLists(studyId),
        ]);

        if (listRes.success)
            setLists(listRes.data || [])

        if (memberRes.success) {
            const members = memberRes.data || [];
            setMemberships(members)
            setDescriptions(members.reduce((acc, m) => {
                acc[m.listId] = m.description || ''
                return acc
            }, {}))
        }
    }

    useEffect(() => {
        load()
    }, [studyId]);

    const memberOf = (listId) => memberships.find(x => x.listId === listId);

    const toggle = async (list, checked) => {
        setBusyListId(list.id)
        try {
            if (checked) {
                const res = await ApiService.StudyList.AddItem({
                    listId: list.id,
                    studyId,
                    description: (descriptions[list.id] || '').trim() || null,
                });

                if (res.success) {
                    setMemberships(prev => [...prev.filter(x => x.listId !== list.id), res.data])
                    onChanged && onChanged()
                }
            } else {
                const res = await ApiService.StudyList.RemoveItem({ listId: list.id, studyId });

                if (res.success) {
                    setMemberships(prev => prev.filter(x => x.listId !== list.id))
                    onChanged && onChanged()
                }
            }
        } finally {
            setBusyListId(null)
        }
    }

    const saveDescription = async (list) => {
        const member = memberOf(list.id);
        if (!member || !member.canRemove)
            return;

        const value = (descriptions[list.id] || '').trim();
        if ((member.description || '') === value)
            return;

        const res = await ApiService.StudyList.UpdateItem({
            listId: list.id,
            studyId,
            description: value || null,
        });

        if (res.success) {
            setMemberships(prev => prev.map(x => x.listId === list.id ? res.data : x))
            onChanged && onChanged()
        }
    }

    const createList = async () => {
        if (!newName.trim())
            return;

        setCreating(true)
        try {
            const res = await ApiService.StudyList.Create({ name: newName.trim(), isPublic: newPublic });
            if (res.success) {
                setLists(prev => [...prev, res.data])
                setNewName('')
                setNewPublic(false)
            }
        } finally {
            setCreating(false)
        }
    }

    return (
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {lists.length === 0 && (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={canCreate ? t('No list yet, create one below') : t('No list yet')}
                />
            )}

            {lists.map(list => {
                const member = memberOf(list.id);
                const isMember = !!member;
                // Ekleme LIST_ITEM_ADD ister; çıkarma/açıklama ise ekleyen + liste sahibi + moderatör ile sınırlı.
                const disabled = busyListId === list.id || (isMember ? !member.canRemove : !canAdd);

                return (
                    <div key={list.id} style={{ marginBottom: 10 }}>
                        <Space align={'center'}>
                            <Checkbox
                                checked={isMember}
                                disabled={disabled}
                                onChange={(e) => toggle(list, e.target.checked)}
                            >
                                {list.name}
                            </Checkbox>
                            {!list.isPublic && <Tag>{t('Private')}</Tag>}
                            {!list.rights?.isOwner && list.ownerUserName && (
                                <Tooltip title={t('List owner')}>
                                    <Typography.Text type={'secondary'} style={{ fontSize: 11 }}>
                                        {list.ownerUserName}
                                    </Typography.Text>
                                </Tooltip>
                            )}
                        </Space>

                        {isMember && (
                            <Input.TextArea
                                style={{ marginTop: 4 }}
                                rows={2}
                                maxLength={500}
                                showCount
                                disabled={!member.canRemove}
                                placeholder={t('Description (optional) - shown on the star tooltip')}
                                value={descriptions[list.id] || ''}
                                onChange={(e) => setDescriptions(prev => ({ ...prev, [list.id]: e.target.value }))}
                                onBlur={() => saveDescription(list)}
                            />
                        )}
                    </div>
                )
            })}

            {canCreate && (
                <>
                    <Divider style={{ margin: '12px 0' }} />
                    <Space direction={'vertical'} style={{ width: '100%' }}>
                        <Input
                            placeholder={t('New list name')}
                            value={newName}
                            maxLength={200}
                            onChange={(e) => setNewName(e.target.value)}
                            onPressEnter={createList}
                        />
                        <Space>
                            <Switch checked={newPublic} onChange={setNewPublic} size={'small'} />
                            <span>{newPublic ? t('Public (everyone with permission sees it)') : t('Private (only you)')}</span>
                        </Space>
                        <Button
                            type={'primary'}
                            size={'small'}
                            icon={<PlusOutlined />}
                            loading={creating}
                            disabled={!newName.trim()}
                            onClick={createList}
                        >
                            {t('Create List')}
                        </Button>
                    </Space>
                </>
            )}
        </div>
    )
}
